<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Planos de Matrículas</h5>
      <b-button
        v-if="hasPermission('adicionar_planos_matriculas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Novo Plano"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por Nome"
        class="form-control"
        @input="filtrar"
      />
      <b-button
        variant="secondary"
        class="d-flex align-items-center"
        @click="limparFiltros"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('id')">
              Plano {{ arrow('id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('categoria')">
              Categoria {{ arrow('categoria') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('tipo')">
              Tipo de Plano {{ arrow('tipo') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('aulas_semana')">
              Aulas/Semana {{ arrow('aulas_semana') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('quantidade_parcelas')">
              Nº Parcelas {{ arrow('quantidade_parcelas') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('valor_parcela')">
              Parcela {{ arrow('valor_parcela') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('ativo')">
              Status {{ arrow('ativo') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="pl in planos" :key="pl.id">
          <td>{{ pl.id }}</td>
          <td>{{ pl.categoria }}</td>
          <td>{{ formatTipo(pl.tipo) }}</td>
          <td class="text-center">{{ pl.aulas_semana }}</td>
          <td class="text-center">{{ pl.quantidade_parcelas }}</td>
          <td>{{ formatCurrency(pl.valor_parcela) }}</td>
          <td>{{ pl.ativo ? 'Ativo' : 'Inativo' }}</td>
          <td class="text-end">
            <router-link
              v-if="hasPermission('detalhar_planos_matriculas')"
              :to="`/cadastros/planos-matriculas/${pl.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-2" title="Detalhar"></i>
            </router-link>
            <router-link
              v-if="hasPermission('alterar_planos_matriculas')"
              :to="`/cadastros/planos-matriculas/${pl.id}`"
            >
              <i class="bi bi-pencil-fill text-warning mx-2" title="Editar"></i>
            </router-link>
            <i
              v-if="hasPermission('excluir_planos_matriculas')"
              class="bi bi-trash-fill text-danger mx-2"
              @click="excluir(pl.id)"
              title="Excluir"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

const router = useRouter()
const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

// tipos de plano (para duração)
const tipos = ref([])
async function loadTipos() {
  const res = await axios.get(`/cadastros/tipos-planos`)
  tipos.value = res.data.data
}
function formatTipo(nome) {
  const t = tipos.value.find(x => x.nome === nome)
  return t ? `${t.nome} (${t.duracao} dias)` : nome
}

// listagem, paginação e ordenação
const {
  items: planos,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/planos-matriculas`,
  { key: 'id', order: 'asc' }
)

const pageOptions = [10, 15, 20, 25, 50]
const startRow = computed(() => (page.value - 1) * limit.value + 1)
const endRow   = computed(() => Math.min(page.value * limit.value, totalItems.value))

watch(page,  (n, o) => n !== o && load())
watch(limit,(n, o) => { if (n !== o) { page.value = 1; load() } })

// filtros
function filtrar() {
  page.value = 1
  load()
}
function limparFiltros() {
  filters.value = { nome: '' }
  page.value = 1
  load()
}
function changeLimit() {
  page.value = 1
  load()
}

// navegação e CRUD
function irParaCadastro() {
  router.push('/cadastros/planos-matriculas/novo')
}
async function excluir(id) {
  const ok = await proxy.$confirm('Tem certeza que deseja excluir este plano?', 'Confirmação')
  if (!ok) return
  await axios.delete(`/cadastros/planos-matriculas/${id}`)
  proxy.$toast.success('Plano excluído com sucesso!')
  load()
}

// moeda
function formatCurrency(val) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val)
}

onMounted(async () => {
  await fetchPermissions()
  await loadTipos()
  filters.value = { nome: '' }
  load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
/* oculta ícone nativo */
.bv-table-sort-icon {
  display: none !important;
}
</style>
